/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { type Meta, type StoryObj } from '@storybook/react-vite';

import { Checkbox } from '../../index';
import {
  CheckboxControlled,
  CheckboxControlledSrc,
  CheckboxDefault,
  CheckboxDefaultSrc,
  CheckboxDisabled,
  CheckboxDisabledSrc,
  CheckboxFull,
  CheckboxFullSrc,
  CheckboxHasLabelBefore,
  CheckboxHasLabelBeforeSrc,
  CheckboxIndeterminate,
  CheckboxIndeterminateSrc,
  CheckboxNoLabel,
  CheckboxNoLabelSrc,
} from '.';

const componentMeta: Meta<typeof Checkbox> = {
  title: 'Components/Checkbox',
  id: 'components-checkbox',
  component: Checkbox,
  tags: ['docsPage'],
  argTypes: {
    ref: {
      description: 'Ref to the `input` element',
    },
  },
  parameters: {
    controls: { disable: true },
  },
  decorators: [
    (Story) => (
      <div className="n-flex n-justify-center">
        <Story />
      </div>
    ),
  ],
};

export default componentMeta;
type Story = StoryObj<typeof componentMeta>;

export const Default: Story = {
  args: {},
  render: CheckboxDefault,
  parameters: {
    docs: {
      source: {
        language: 'tsx',
        type: 'code',
        code: CheckboxDefaultSrc,
      },
    },
  },
};

export const Indeterminate: Story = {
  args: {},
  render: CheckboxIndeterminate,
  parameters: {
    docs: {
      source: {
        language: 'tsx',
        type: 'code',
        code: CheckboxIndeterminateSrc,
      },
    },
  },
};

export const NoLabel: Story = {
  args: {},
  render: CheckboxNoLabel,
  parameters: {
    docs: {
      source: {
        language: 'tsx',
        type: 'code',
        code: CheckboxNoLabelSrc,
      },
    },
  },
};

export const HasLeadingLabel: Story = {
  args: {},
  render: CheckboxHasLabelBefore,
  parameters: {
    docs: {
      source: {
        language: 'tsx',
        type: 'code',
        code: CheckboxHasLabelBeforeSrc,
      },
    },
  },
};

export const Disabled: Story = {
  args: {},
  render: CheckboxDisabled,
  parameters: {
    docs: {
      source: {
        language: 'tsx',
        type: 'code',
        code: CheckboxDisabledSrc,
      },
    },
  },
};

export const Full: Story = {
  args: {},
  render: CheckboxFull,
  parameters: {
    docs: {
      source: {
        language: 'tsx',
        type: 'code',
        code: CheckboxFullSrc,
      },
    },
  },
};

export const Controlled: Story = {
  args: {},
  parameters: {
    docs: {
      source: {
        code: CheckboxControlledSrc,
        language: 'tsx',
        type: 'code',
      },
    },
  },
  render: CheckboxControlled,
};
